import PropTypes from "prop-types";
import React from "react";
import GoogleMapsLoader from "google-maps";
import _ from "lodash";
import Measure from "react-measure";

import { Markers, Polylines } from "./";

GoogleMapsLoader.KEY = "AIzaSyBJ5gh-HqMPqJRm81QaXbE825CEH_ECZLo";
GoogleMapsLoader.LIBRARIES = ["geometry", "places"];
GoogleMapsLoader.VERSION = "3.38";



/**
 * -Map-
 * Map component
 **/
class Map extends React.Component {
  state = {
    mapReady: false,
    google: {},
    map: {},
    currentLocation: {
      lat: parseFloat(this.props.initialLocation.lat),
      lng: parseFloat(this.props.initialLocation.lng),
    },
    zoom: this.props.zoom,
    dimensions: {},
    manualBounds: [],
  };

  // Used with sidebar toggle
  updateSize = () => {
    const { google, map } = this.state;

    google.maps && google.maps.event.trigger(map, "resize");

    google.maps && this.resetPosition();
  };

  resetPosition = () => {
    const { map, currentLocation, zoom } = this.state;

    map.setCenter(currentLocation);

    map.setZoom(zoom);
  };

  fitBounds = () => {
    // Fit all markers if fit bounds is true
    if (this.props.fitBounds) {
      const { google, map } = this.state;

      let bounds = new google.maps.LatLngBounds();

      let markers = [];
      let polylines = [];

      React.Children.forEach(this.props.children, (child) => {
        if (child) {
          // Get all markers
          const fitToMarkers =
            this.props.fitBounds.markers === true ||
            this.props.fitBounds.markers.length > 0;
          if (fitToMarkers && child.type === Markers) {
            Object.keys(child.props.markers).forEach((markerGroup) => {
              const fitToGroup =
                this.props.fitBounds.markers === true ||
                _.find(
                  this.props.fitBounds.markers,
                  (marker) => marker.indexOf(markerGroup) !== -1
                );

              if (fitToGroup) {
                const markersOfGroup = child.props.markers[markerGroup].map(
                  (marker) => marker
                );
                markers.push(...markersOfGroup);
              }
            });
          }

          // Get all polylines
          const fitToPolylines =
            this.props.fitBounds.polylines === true ||
            this.props.fitBounds.polylines.length > 0;
          if (fitToPolylines && child.type === Polylines) {
            Object.keys(child.props.polylines).forEach((polylineGroup) => {
              const fitToGroup =
                this.props.fitBounds.polylines === true ||
                _.find(
                  this.props.fitBounds.polylines,
                  (polyline) => polyline.indexOf(polylineGroup) !== -1
                );

              if (fitToGroup) {
                const polylinesOfGroup = child.props.polylines[
                  polylineGroup
                ].map((polyline) => polyline);
                polylines.push(...polylinesOfGroup);
              }
            });
          }
        }
      });

      if (markers.length > 0 || polylines.length > 0) {
        // Extend bounds by markers
        markers.length > 0 &&
          markers.forEach((marker) => {
            bounds.extend({
              lat: parseFloat(marker.location.lat),
              lng: parseFloat(marker.location.lng),
            });
          });

        // Extend bounds by polylines
        polylines.length > 0 &&
          polylines.forEach((polyline) => {
            // Format path, decode if encoded
            const path = Array.isArray(polyline.path)
              ? polyline.path
              : google.maps.geometry.encoding.decodePath(polyline.path);

            path.forEach((point) => {
              bounds.extend({
                lat: parseFloat(point.lat),
                lng: parseFloat(point.lng),
              });
            });
          });

        // Ensure max zoom not exceeded
        google.maps.event.addListenerOnce(map, "bounds_changed", () => {
          if (map.getZoom() > this.props.maxZoom) {
            map.setZoom(this.props.maxZoom);
          }

          // Used to prevent optimised:false markers disappearing on panTo
          google.maps.event.addListenerOnce(map, "idle", () => {
            map.panTo(map.getCenter());
          });
        });

        this.state.map.fitBounds(bounds);
      } else {
        if (this.state.manualBounds.length === 0) {
          this.resetPosition();
        }
      }
    }
  };

  addNewMarker = (e) => {
    // Determine the lat and lng of where the user clicked to drop a pin
    const position = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };

    const content = null;
    const title = null;
    
    // Call the overlay where the user clicked
    const overlays = [{ content, position, title }];
    this.props.addOverlay("sitewide", overlays);
  };

  fitManualBounds = () => {
    const { google /* map */ } = this.state;
    const polylines = this.state.manualBounds;

    if (!google.maps) {
      return;
    }

    let bounds = new google.maps.LatLngBounds();

    // Extend bounds by polylines
    polylines.length > 0 &&
      polylines.forEach((polyline) => {
        // Format path, decode if encoded
        const path = Array.isArray(polyline.path)
          ? polyline.path
          : google.maps.geometry.encoding.decodePath(polyline.path);

        path.forEach((point) => {
          bounds.extend({
            lat: parseFloat(point.lat),
            lng: parseFloat(point.lng),
          });
        });
      });

    this.state.map.fitBounds(bounds);
  };

  loadMap = () => {
    return new Promise((resolve) => {
      GoogleMapsLoader.load((google) => {
        const map_id = this.refs.map;

        let map_options = {
          center: {
            lat: this.state.currentLocation.lat,
            lng: this.state.currentLocation.lng,
          },
          backgroundColor: "hsla(0, 0%, 0%, 0)",
          zoom: parseInt(this.props.zoom, 10),
          minZoom: 8,
          maxZoom: 17,
          clickableIcons: false,
          mapTypeControl: false,
          zoomControl: false,
          disableDoubleClickZoom: true,
          fullscreenControl: false,
          streetViewControl: true,
          gestureHandling: "cooperative",
          mapId: "65da1c4715f7b67",
        };

        // Merge options if passed
        map_options = { ...map_options, ...this.props.options };

        const map = new google.maps.Map(map_id, map_options);
        window.map = map;
        map.streetView.addListener("visible_changed", () => {
          this.props.setInStreetView(map.streetView.visible);
        });

        this.setState(
          {
            mapReady: true,
            google,
            map,
          },
          () => {
            if (this.state.manualBounds.length) {
              this.fitManualBounds();
            }
          }
        );

        // Keep track of center
        google.maps.event.addListener(map, "dragend", () => {
          const center = map.getCenter();
          const lat = center.lat();
          const lng = center.lng();

          this.setState({
            currentLocation: { lat, lng },
          });
        });

        // Keep track of zoom
        google.maps.event.addListener(map, "zoom_changed", () => {
          const zoom = map.getZoom();

          this.setState({
            zoom,
          });
        });

        google.maps.event.addListener(map, "dblclick", (e) => {
          if (this.props.cycleRoutes.gotData === false || Object.keys(this.props.cycleRoutes).length === 0) {
            this.addNewMarker(e);
          }
        });


        // Open an infowindow on right click so the user can add a location
        google.maps.event.addListener(map, "rightclick", (e) => {
            if (this.props.cycleRoutes.gotData === false || Object.keys(this.props.cycleRoutes).length === 0) {
            this.addNewMarker(e);
          }
        });

        resolve(google);
      });
    });
  };

  componentDidUpdate(prevProps, prevState) {
    const { mapReady } = this.state;

    // Determine whether to fitBounds
    const newElements = !_.isEqual(this.props.map, prevProps.map);
    if (newElements && mapReady) {
      let oldMarkers, newMarkers, oldPolylines, newPolylines;

      React.Children.forEach(this.props.children, (child) => {
        if (child) {
          if (child.type === Markers) {
            newMarkers = child.props.markers;
          }

          if (child.type === Polylines) {
            newPolylines = child.props.polylines;
          }
        }
      });

      React.Children.forEach(prevProps.children, (child) => {
        if (child) {
          if (child.type === Markers) {
            oldMarkers = child.props.markers;
          }

          if (child.type === Polylines) {
            oldPolylines = child.props.polylines;
          }
        }
      });

      // Always ignore bike parking
      newMarkers = _.omit(newMarkers, ["poi_bikeParking"]);
      oldMarkers = _.omit(oldMarkers, ["poi_bikeParking"]);
      const markersUpdated = !_.isEqual(newMarkers, oldMarkers);
      const polylinesUpdated = !_.isEqual(newPolylines, oldPolylines);
      if (markersUpdated || polylinesUpdated) {
        this.fitBounds();
      }
    }

    const newMapReady = !_.isEqual(this.state.mapReady, prevState.mapReady);
    if (newMapReady) {
      this.fitBounds();
    }

    const newSize = !_.isEqual(this.state.dimensions, prevState.dimensions);
    if (newSize) {
      this.updateSize();
    }

    if (this.props.map.bounds.length !== this.state.manualBounds.length) {
      const manualBounds = this.props.map.bounds;

      if (manualBounds.length) {
        this.setState(
          {
            manualBounds,
          },
          () => {
            if (this.state.manualBounds.length) {
              this.fitManualBounds();
            }
          }
        );
      }
    }
  }

  componentDidMount() {
    this.loadMap();
  }

  render() {
    return (
      <Measure
        onMeasure={(dimensions) =>
          this.setState({ dimensions: { ...dimensions } })
        }
      >
        <div ref="map" className={this.props.class}>
          Loading...
          {this.state.mapReady &&
            React.Children.map(this.props.children, (child) => {
              return child && React.cloneElement(child, this.state);
            })}
        </div>
      </Measure>
    );
  }
}

export default Map;

export { default as Markers } from "./Markers";
export { default as Marker } from "./Marker";
export { default as Polylines } from "./Polylines";
export { default as Polygons } from "./Polygons";
export { default as Overlays } from "./Overlays";
export { default as Controls } from "./Controls";

Map.propTypes = {
  class: PropTypes.string,
  initialLocation: PropTypes.object,
  zoom: PropTypes.number,
  maxZoom: PropTypes.number,
  fitBounds: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  disableDoubleClickZoom: PropTypes.bool,
};

Map.defaultProps = {
  class: "map",
  initialLocation: {
    lat: 51.454549,
    lng: -2.587919,
  },
  zoom: 12,
  maxZoom: 15,
  fitBounds: false,
  disableDoubleClickZoom: true,
};



// WEBPACK FOOTER //
// ./src/components/googlemaps/index.js